import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SpaFooter, SpaNav } from './SpaHome';
import { publicApi } from './publicApi';
import { brandPath, locationForBrand, usePublicBrand } from './PublicBrand';
import { dateOf, timeOf } from '../shopTime';
import Seo from './Seo';
import './spa.css';

const money = (value) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(Number(value || 0));

const FINAL = new Set(['succeeded', 'failed', 'expired', 'cancelled']);

export default function SpaBookingPayment() {
  const { brand } = usePublicBrand();
  const location = locationForBrand(brand);
  const [params] = useSearchParams();
  const token = params.get('session');
  const [checkout, setCheckout] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    let timer;

    const read = async () => {
      if (!token) {
        setError('This payment link is incomplete.');
        return;
      }
      try {
        const result = await publicApi.bookingPayment(token);
        if (!live) return;
        setCheckout(result.checkout);
        setError(null);
        if (!FINAL.has(result.checkout.status)) timer = setTimeout(read, 2_000);
      } catch (requestError) {
        if (!live) return;
        setError(requestError.message);
        timer = setTimeout(read, 4_000);
      }
    };

    read();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [token]);

  const paid = checkout?.status === 'succeeded';
  const failed = checkout && ['failed', 'expired', 'cancelled'].includes(checkout.status);

  return (
    <div className={`spa${brand === 'nirana' ? ' spa-nirana' : ''}${brand === 'saanti-boutique' ? ' spa-boutique' : ''}`}>
      <Seo
        title={`Booking Payment Status | ${location.label}`}
        description={`Private payment status for your ${location.label} reservation.`}
        canonicalPath={brandPath('/booking/payment', brand)}
        noindex
      />
      <SpaNav />
      <section className="spa-section spa-payment-return">
        <div className="wrap">
          <div className="spa-panel spa-done">
            {!checkout && !error && (
              <>
                <div className="spa-payment-spinner" aria-hidden="true" />
                <h2>Checking your payment</h2>
                <p className="spa-note">Please keep this page open for a moment.</p>
              </>
            )}

            {error && !checkout && (
              <>
                <div className="spa-payment-mark failed">!</div>
                <h2>We could not verify this payment</h2>
                <p className="spa-note">{error}</p>
                <Link className="spa-cta spa-link-button" to={brandPath('/book', brand)}>Start a new booking</Link>
              </>
            )}

            {checkout && !paid && !failed && (
              <>
                <div className="spa-payment-spinner" aria-hidden="true" />
                <h2>Payment is being confirmed</h2>
                <p className="spa-note">
                  Beam is still confirming the transaction. This page updates automatically.
                </p>
                <div className="spa-code">{checkout.bookingCode}</div>
              </>
            )}

            {paid && (
              <>
                <div className="tick">✓</div>
                <div className="eyebrow">Payment received</div>
                <h2>Thank you — we have your request</h2>
                <p className="spa-note">
                  Reception will allocate your therapist and room, then contact you to confirm.
                  Please keep this reference.
                </p>
                <div className="spa-code">{checkout.bookingCode}</div>
                <CheckoutSummary checkout={checkout} />
                <Link className="spa-cta spa-link-button" to={brandPath('/', brand)}>Back to home</Link>
              </>
            )}

            {failed && (
              <>
                <div className="spa-payment-mark failed">!</div>
                <h2>Payment was not completed</h2>
                <p className="spa-note">
                  No booking has been sent to reception and the temporary time hold has been released.
                  Please try again.
                </p>
                <div className="spa-code">{checkout.bookingCode}</div>
                <Link className="spa-cta spa-link-button" to={brandPath('/book', brand)}>Book again</Link>
              </>
            )}
          </div>
        </div>
      </section>
      <SpaFooter />
    </div>
  );
}

function CheckoutSummary({ checkout }) {
  return (
    <div className="spa-summary spa-payment-summary">
      <div><span>Branch</span><b>{checkout.branchName}</b></div>
      <div>
        <span>Treatment</span>
        <b>
          {(checkout.treatments || [])
            .map((item) => `${item.name} · ${item.durationMinutes} min`)
            .join(', ') || '-'}
        </b>
      </div>
      <div>
        <span>When</span>
        <b>{dateOf(checkout.scheduledAt)} · {timeOf(checkout.scheduledAt)}</b>
      </div>
      <div><span>Guests</span><b>{checkout.partySize}</b></div>
      <div className="total"><span>Paid with Beam</span><b>{money(checkout.amount)} ฿</b></div>
    </div>
  );
}
